import { useLayoutEffect, useMemo, useRef, useState } from "react"

// ============================================================================
// tog'a #4153 (+++ #4181, 2026-10-13): «Jadvalda ma'lumot ko'p bo'lsa — shapka va eng pastdagi itog qatori joyida qolib,
// faqat ichidagi qatorlar harakatlansin.»
// antd 4.22 `sticky` rejimi: ustun sarlavhasi ilova sarlavhasi ostiga yopishadi, `Table.Summary fixed="bottom"` («Итого»)
// ekran pastiga yopishadi, gorizontal aylantirish chizig'i ham ekran pastida («Итого» ustida) turadi.
// Qayerda turganiga qarab (bir marta, chizishdan oldin) sozlanadi:
//  • oddiy sahifa — sahifa OYNADA aylanadi: sarlavha `--hdr-h` (ilova sarlavhasi balandligi, Header.tsx yozadi) pastida;
//  • drawer / modal ichida — o'sha oyna aylanadi: sarlavha uning tepasiga (0) yopishadi;
//  • ichma-ich jadval (boshqa jadval qatori ichida) — o'chiq: tashqi jadval sarlavhasi bilan ustma-ust tushmasin.
// Oddiy sahifada `<html>` ga `tbl-sticky-page` klassi qo'yiladi (index.less): .site-layout va .site-layout-background
// overflow'i sticky'ni o'ziga bog'lab qo'yardi (ular aylanmaydi — sahifa oynada aylanadi). Faqat shunday jadval bor sahifada.
//
// USTUN ENI (jonli o'lchovda topildi): sticky'da sarlavha va «Итого» ALOHIDA jadvallarda, qatorlar jadvali ularni «ko'rmaydi».
// rc-table bunda jadvalni `table-layout: fixed` qiladi — ustunlar siqilib, sarlavhalar 3–4 qatorga bo'linardi, «Итог [N]»
// qiymat ustiga chiqardi. Shuning uchun: `tableLayout: auto` (avvalgidek tabiiy en) va rc-table'ning ko'rinmas o'lchov qatoriga
// (measure row, balandligi 0) har ustun sarlavhasining «arvoh» nusxasi (o'sha matn, shrift, belgilar eni) va «Итого» katagining
// eni qo'yiladi — brauzer ustun enini eski BITTA jadvaldagidek hisoblaydi (eng uzun so'z, butun nom, qatorlar mazmuni).
// Eski build bilan ustunma-ust solishtirildi (cmp_4153.js).
// ============================================================================

export interface StickyConf {
  offsetHeader?: number
  offsetSummary?: number
  offsetScroll?: number
  getContainer?: () => HTMLElement | Window
}

let pageTables = 0   // oynada aylanadigan qotgan jadvallar soni (sahifada bir nechta bo'lishi mumkin)

const hdrH = () => {
  const v = parseInt(getComputedStyle(document.documentElement).getPropertyValue("--hdr-h"), 10)
  return Number.isFinite(v) && v > 0 ? v : 64
}
const CONTAINER = ":scope > .ant-spin-nested-loading > .ant-spin-container > .ant-table > .ant-table-container"
const xizmat = (th: Element) => th.classList.contains("ant-table-selection-column") || th.classList.contains("ant-table-row-expand-icon-cell")

export default function useStickyTable(enabled: boolean = true) {
  const ref = useRef<HTMLDivElement>(null)
  const [conf, setConf] = useState<StickyConf | false>(false)

  // (1) qayerda turibdi: sahifa / drawer / modal / ichma-ich
  useLayoutEffect(() => {
    const el = ref.current
    if (!enabled || !el) {
      setConf(false)
      return
    }
    if (el.parentElement?.closest(".ant-table")) {
      setConf(false)
      return
    }
    const box = el.closest(".ant-drawer-body, .ant-modal-wrap") as HTMLElement | null
    if (box) {
      // sticky oyna padding'idan ICHKARIDA yopishadi (drawer body — 24 px): sarlavha ustida bo'shliq qolib, qatorlar undan ko'rinardi
      // (jonli o'lchovda topildi). Manfiy siljish — oyna chetiga zich.
      const cs = getComputedStyle(box)
      const pt = parseFloat(cs.paddingTop) || 0, pb = parseFloat(cs.paddingBottom) || 0
      setConf({ offsetHeader: -pt, offsetSummary: -pb, getContainer: () => box })
      return
    }
    // Demo (tog'a #138, 2026-10-19): jadval o'z «overflow: hidden/auto» qutisi ichida bo'lsa (masalan «Клиенты» sahifasidagi Card —
    // burchak radiusi uchun overflow: hidden), position: sticky o'sha qutiga bog'lanadi: quti aylanmagani uchun sarlavha `top` qadar
    // PASTGA siljib, birinchi qator(lar)ni yopib qo'yardi (UI sinovi: magazin «Клиенты» — 1 ta mijoz ko'rinmas, tugmalari bosilmasdi).
    // Bunday qutida qotish baribir ishlamaydi — o'chiriladi (eskidek oddiy jadval). .site-layout(-background) — pastdagi klass ochadi.
    for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
      if (p.classList.contains("site-layout") || p.classList.contains("site-layout-background")) continue
      const cs = getComputedStyle(p)
      if (/hidden|auto|scroll/.test(cs.overflowX + " " + cs.overflowY)) {
        setConf(false)
        return
      }
    }
    const upd = () => setConf((c) => (c && c.offsetHeader === hdrH() ? c : { offsetHeader: hdrH() }))
    upd()
    pageTables++
    document.documentElement.classList.add("tbl-sticky-page")
    // ilova sarlavhasi balandligi o'zgarsa (planshetda 2 qatorga o'raladi) — Header.tsx `--hdr-h` ni <html style> ga yozadi
    const mo = new MutationObserver(upd)
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["style"] })
    return () => {
      mo.disconnect()
      if (--pageTables <= 0) {
        pageTables = 0
        document.documentElement.classList.remove("tbl-sticky-page")
      }
    }
  }, [enabled])

  // (2) «Итого» balandligi — gorizontal aylantirish chizig'i uning ustida tursin (ustma-ust tushmasin);
  // (3) ustunlarning minimal eni — sarlavha va «Итого» kataklari sig'sin (yuqoridagi izoh)
  const [sumH, setSumH] = useState(0)
  useLayoutEffect(() => {
    const el = ref.current
    if (!conf || !el) {
      setSumH(0)
      return
    }
    const c = el.querySelector(CONTAINER) as HTMLElement | null
    if (!c) return
    let raf = 0
    const olcha = () => {
      raf = 0
      const hdr = c.querySelector(":scope > .ant-table-header") as HTMLElement | null
      const sum = c.querySelector(":scope > .ant-table-summary") as HTMLElement | null
      const body = c.querySelector(":scope > .ant-table-body") as HTMLElement | null
      setSumH(sum ? Math.round(sum.getBoundingClientRect().height) : 0)
      const hRows = hdr ? hdr.querySelectorAll(":scope > table > thead > tr") : null
      const mRow = body ? body.querySelector(":scope > table > tbody > tr.ant-table-measure-row") : null
      if (!hdr || !hRows || hRows.length !== 1 || !mRow) return   // guruhlangan (ko'p qatorli) sarlavha — o'lchanmaydi
      const th = Array.from(hRows[0].children) as HTMLElement[]
      const mc = Array.from(mRow.children) as HTMLElement[]
      if (mc.length !== th.length) return
      if (!th.some((x) => x.getBoundingClientRect().width > 0)) return   // ko'rinmas (yopiq oyna / tab) — ko'ringanda qayta o'lchanadi
      // «Итого» kataklari bir qatorli (nowrap) — tabiiy eni bir lahza o'lchanadi (ekranga chizilmaydi)
      const sw: number[] = []
      const sRow = sum ? sum.querySelector(":scope > table > tfoot > tr") : null
      const td = sRow ? (Array.from(sRow.children) as HTMLTableCellElement[]) : []
      if (sum && td.length === th.length && td.every((x) => x.colSpan === 1)) {
        sum.classList.add("tbl-olchov", "tbl-olchov-keng")
        td.forEach((x, i) => { sw[i] = Math.ceil(x.getBoundingClientRect().width) })
        sum.classList.remove("tbl-olchov", "tbl-olchov-keng")
      }
      // padding + chegara (ilova CSS'i qator kataklariga — o'lchov katagiga ham — `padding: 0 12px !important` beradi)
      const pc = (s: CSSStyleDeclaration) => (parseFloat(s.paddingLeft) || 0) + (parseFloat(s.paddingRight) || 0) + (parseFloat(s.borderLeftWidth) || 0) + (parseFloat(s.borderRightWidth) || 0)
      mc.forEach((cell, i) => {
        const d = cell.firstElementChild as HTMLElement | null
        if (!d) return
        let g = d.querySelector(":scope > .tbl-arvoh") as HTMLElement | null
        let s = d.querySelector(":scope > .tbl-arvoh-s") as HTMLElement | null
        if (xizmat(th[i])) { g?.remove(); s?.remove(); return }
        // «arvoh» sarlavha: o'sha matn, o'sha shrift va belgilar eni — eski bitta jadvaldagidek ustun eniga qatnashadi
        // (eng uzun so'z — minimal, butun nom — tabiiy en), lekin balandlik egallamaydi (o'lchov katagi 0 px, overflow hidden)
        const h = getComputedStyle(th[i]), m = getComputedStyle(cell)
        let ikon = 0
        th[i].querySelectorAll(".ant-table-column-sorter, .ant-table-filter-trigger").forEach((e) => {
          const es = getComputedStyle(e)
          ikon += e.getBoundingClientRect().width + (parseFloat(es.marginLeft) || 0) + (parseFloat(es.marginRight) || 0)
        })
        if (!g) {
          g = document.createElement("div"); g.className = "tbl-arvoh"; g.setAttribute("aria-hidden", "true")
          g.appendChild(document.createElement("span")); g.appendChild(document.createElement("i"))
          d.appendChild(g)
        }
        const matn = (th[i].textContent || "").replace(/\s+/g, " ").trim()
        const sp = g.firstElementChild as HTMLElement, ic = g.lastElementChild as HTMLElement
        if (sp.textContent !== matn) sp.textContent = matn
        Object.assign(g.style, {
          display: "flex", fontFamily: h.fontFamily, fontSize: h.fontSize, fontWeight: h.fontWeight, fontStyle: h.fontStyle,
          letterSpacing: h.letterSpacing, textTransform: h.textTransform, whiteSpace: h.whiteSpace,
          // piksel kasri: yaxlitlashda 0,4 px yo'qolsa chegarada sig'adigan sarlavha 2 qatorga tushardi («Товары» — o'lchovda) → +1 px zaxira
          marginRight: Math.ceil(pc(h) - pc(m)) + 1 + "px",
        })
        Object.assign(ic.style, { flex: "none", width: Math.ceil(ikon) + "px" })
        // «Итого» katagi: bir qatorli tabiiy eni (eski jadvalda ham shunday edi)
        if (sw[i] > 0) {
          if (!s) { s = document.createElement("div"); s.className = "tbl-arvoh-s"; s.setAttribute("aria-hidden", "true"); d.appendChild(s) }
          const v = Math.max(0, sw[i] - pc(m)) + "px"
          if (s.style.width !== v) s.style.width = v
        } else s?.remove()
      })
    }
    olcha()
    // sarlavha (ustunlar, til) yoki «Итого» o'zgarsa — qayta (bir kadrda bir marta). Qatorlar ichidagi o'zgarishlar (masalan har
    // soniyada yangilanadigan sanoq) e'tiborsiz: ular minimal enga ta'sir qilmaydi; ustunlar o'zgarsa sarlavha ham o'zgaradi.
    const qayta = () => { if (!raf) raf = requestAnimationFrame(olcha) }
    const mo = new MutationObserver((recs) => {
      const body = c.querySelector(":scope > .ant-table-body")
      if (recs.some((r) => !body || !body.contains(r.target))) qayta()
    })
    mo.observe(c, { childList: true, subtree: true, characterData: true })
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(qayta) : null
    ro?.observe(c)
    return () => {
      mo.disconnect()
      ro?.disconnect()
      if (raf) cancelAnimationFrame(raf)
    }
  }, [conf])

  // gorizontal chiziq «Итого» ustida: oyna chetidan «Итого» balandligicha yuqorida (drawer'da padding hisobga olinadi)
  const sticky = useMemo<StickyConf | undefined>(() => (conf ? { ...conf, offsetScroll: Math.max(0, sumH + (conf.offsetSummary || 0)) } : undefined), [conf, sumH])
  return { ref, sticky }
}

/**
 * rc-table sticky'SIZ qanday `table-layout` tanlardi (rc-table Table.js → mergedTableLayout). Sticky bo'lsa u har doim `fixed`
 * qiladi — ustunlar siqiladi; shu funksiya eski (sticky'siz) tanlovni qaytaradi: fixed ustun bo'lsa — scroll.x "max-content"da
 * auto, aks holda fixed (ustun enlari berilgan jadvallar, masalan «Тенанты» — o'zgarmaydi); ellipsis ustun — fixed; qolgani — auto.
 */
export function asliLayout(columns: any[] | undefined, scrollX: any, boshqaFixed?: boolean): "auto" | "fixed" {
  const tekis: any[] = []
  const yur = (cs: any[] | undefined) => (cs || []).forEach((c) => (c && c.children ? yur(c.children) : tekis.push(c)))
  yur(columns)
  if (boshqaFixed || tekis.some((c) => c?.fixed)) return scrollX === "max-content" ? "auto" : "fixed"
  if (tekis.some((c) => c?.ellipsis)) return "fixed"
  return "auto"
}
